import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { buildTioAuthHeaders } from '../utils/auth-headers.ts';
import downloadFile from '../utils/download-file.ts';
import type { PaymentManner } from '../data/resources/tas-payment.type.ts';

const tasPaymentsUrl = (path?: string) => {
  const opts: QueryUrlOptions = {
    identifier: { type: 'tas-payments' },
    op: 'query',
    resourcePath: 'tas-payments',
  };
  const base = buildBaseURL(opts);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
};

/** POST /tas-payments/create_payment — admin only. */
export function createTasPaymentRequest(
  companyId: string,
  paymentManner?: PaymentManner
): RequestInfo {
  return {
    url: tasPaymentsUrl('create_payment'),
    method: 'POST',
    body: JSON.stringify({
      company_id: companyId,
      ...(paymentManner && { payment_manner: paymentManner }),
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/** POST /tas-payments/make_payment — admin only. Create + settle. */
export function createAndSettleTasPaymentRequest(
  companyId: string,
  paymentManner?: PaymentManner
): RequestInfo {
  return {
    url: tasPaymentsUrl('make_payment'),
    method: 'POST',
    body: JSON.stringify({
      company_id: companyId,
      ...(paymentManner && { payment_manner: paymentManner }),
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/** PUT /tas-payments/:id/settle_payment — admin + employee admin views. */
export function settleTasPaymentRequest(paymentId: string): RequestInfo {
  return {
    url: tasPaymentsUrl(`${paymentId}/settle_payment`),
    method: 'PUT',
    cacheOptions: { [SkipCache]: true },
  };
}

/** DELETE /tas-payments/:id/cancel_payment — admin + employee admin views. */
export function cancelTasPaymentRequest(paymentId: string): RequestInfo {
  return {
    url: tasPaymentsUrl(`${paymentId}/cancel_payment`),
    method: 'DELETE',
    cacheOptions: { [SkipCache]: true },
  };
}

/* -------------------------------------------------------------------------
 * BLOB download — bypasses JSON:API content negotiation, so it is a raw
 * `fetch` helper (NOT `store.request`) using `buildTioAuthHeaders`, mirroring
 * the established Phase 2.x download pattern (see `requests/search.ts`).
 * ------------------------------------------------------------------------- */

/** Build the payment-files download URL: `{host}/tas-payments/{id}/download_files`.
 * Mirrors the admin payments page's
 * `[adapter.buildURL('tas-payments'), paymentId, 'download_files'].join('/')`. */
export function tasPaymentDownloadFilesUrl(paymentId: string): string {
  return tasPaymentsUrl(`${paymentId}/download_files`);
}

/**
 * GET /tas-payments/:id/download_files — stream the payment files as a ZIP and
 * trigger a browser download. Replaces the inline raw-`fetch` in the admin
 * payments page that read `adapter.headers` + `adapter.buildURL('tas-payments')`.
 *
 * Headers match the legacy application adapter's `headers` bundle EXACTLY:
 * x-api-key (from the caller's per-app `ENV.apiKey`), plus x-auth-key /
 * tio-auth-token / Time-Zone / X-Request-Id from auth-context. The adapter set
 * NO Content-Type or Accept on a GET, so none are sent here. On `response.ok`
 * the helper triggers `downloadFile` inline exactly as the page did
 * (`tas_payment_files.zip`, `application/x-zip`).
 *
 * @param paymentId the tas-payment id
 * @param apiKey    the per-app `ENV.apiKey` (tio-data is owner-free — see auth-headers)
 */
export async function downloadTasPaymentFiles(
  paymentId: string,
  { apiKey }: { apiKey: string }
): Promise<void> {
  const headers = buildTioAuthHeaders({ apiKey });

  const response = await fetch(tasPaymentDownloadFilesUrl(paymentId), { headers });
  if (response.ok) {
    downloadFile(await response.blob(), 'tas_payment_files.zip', 'application/x-zip');
  }
}
